import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarSpace,
} from '@backstage/core-components';

import { NavContentBlueprint } from '@backstage/plugin-app-react';
import type { BasicPermission } from '@backstage/plugin-permission-common';
import { usePermission } from '@backstage/plugin-permission-react';
import {
  ksdpAppsecReadPermission,
  ksdpChecklistReadPermission,
  ksdpComplianceMgmtReadPermission,
  ksdpExceptionsReadPermission,
  ksdpGrrReadPermission,
  ksdpRiskReadPermission,
} from '@internal/ksdp-permissions-common';

import { SidebarLogo } from './SidebarLogo';
// Notifications are not used for now; re-enable together with the item below.
// import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { CustomSecurityIcon } from './SecurityIcon';
import { CustomComplianceIcon } from './ComplianceIcon';
import { CustomExceptionsIcon } from './ExceptionsIcon';
import { CustomRiskLibraryIcon } from './RiskLibraryIcon';
import { ChecklistApprovalIcon } from './ApprovalIcon';
import PublicIcon from '@material-ui/icons/Public';

const sidebarStyle = `
  .MuiDrawer-paper {
    background-color: #FFFFFF !important;
    border-right: 1px solid #E7EBF0 !important;
  }

  /*
   * SidebarItem uses BackstageSidebarItem-root/selected classes, not
   * Material UI's .MuiListItem-root/.Mui-selected classes. Targeting the
   * actual Backstage classes keeps built-in and custom entries consistent.
   */
  .BackstageSidebarItem-root {
    border-radius: 8px;
    margin: 2px 8px;
    color: #8A8F98 !important;
    transition: color 120ms ease, background-color 120ms ease;
  }

  .BackstageSidebarItem-root:hover {
    background-color: #F3EEE6 !important;
    color: #1450F5 !important;
  }

  /* The active route remains blue after the pointer leaves the item. */
  .BackstageSidebarItem-root.BackstageSidebarItem-selected,
  .BackstageSidebarItem-root.BackstageSidebarItem-selected:hover {
    background-color: transparent !important;
    color: #1450F5 !important;
  }

  /* Apply the inherited state color to both Material UI and custom SVG icons. */
  .BackstageSidebarItem-root svg,
  .BackstageSidebarItem-root .MuiSvgIcon-root {
    color: inherit !important;
  }
`;

/**
 * A KSDP sidebar entry, shown only to users holding the plugin's read
 * permission. UI-only: the page itself is guarded too, and the backend
 * routers enforce every permission.
 */
const KsdpSidebarItem = ({
  permission,
  ...item
}: { permission: BasicPermission } & Parameters<typeof SidebarItem>[0]) => {
  const { allowed } = usePermission({ permission });
  return allowed ? <SidebarItem {...item} /> : null;
};

export const SidebarContent: any = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      return (
        <>
          <style>{sidebarStyle}</style>
          <Sidebar>
            <SidebarLogo />

            <SidebarSpace />

            <SidebarDivider />
            <KsdpSidebarItem
              permission={ksdpAppsecReadPermission}
              icon={() => <CustomSecurityIcon />}
              to="/business-applications"
              text="Application Security View"
            />
            <KsdpSidebarItem
              permission={ksdpComplianceMgmtReadPermission}
              icon={() => <CustomComplianceIcon />}
              to="/compliance-management"
              text="Compliance Management"
            />
            <KsdpSidebarItem
              permission={ksdpExceptionsReadPermission}
              icon={() => <CustomExceptionsIcon />}
              to="/exceptions"
              text="Exceptions"
            />
            <KsdpSidebarItem
              permission={ksdpChecklistReadPermission}
              icon={() => <ChecklistApprovalIcon />}
              to="/checklist-approval"
              text="Audit Checklist Approval"
            />
            <KsdpSidebarItem
              permission={ksdpRiskReadPermission}
              icon={() => <CustomRiskLibraryIcon />}
              to="/risk-library"
              text="Risk Library"
            />
            <KsdpSidebarItem
              permission={ksdpGrrReadPermission}
              icon={() => <PublicIcon />}
              to="/grr"
              text="Local Security"
            />

            <SidebarDivider />
            {/* Notifications are not used for now.
            <NotificationsSidebarItem />
            <SidebarDivider />
            */}

            <SidebarSpace />
            <SidebarDivider />
            <SidebarGroup
              label="Settings"
              icon={<UserSettingsSignInAvatar />}
              to="/settings"
            >
              {nav.take('page:user-settings')}
            </SidebarGroup>
          </Sidebar>
        </>
      );
    },
  },
});
